import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { UserTable } from "@/components/admin/users/UserTable";
import { UserFilters } from "@/components/admin/users/UserFilters";
import { User } from "@/components/admin/users/types";

export default function AdminUsers() {
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0); // Trigger for manual refresh
  
  // Filter states
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    let query = supabase.from("profiles").select("*").order("created_at", { ascending: false });

    if (search) {
      query = query.or(`email.ilike.%${search}%,name.ilike.%${search}%`);
    }

    if (status !== 'all') {
       if (status === 'pro') {
           query = query.eq('subscription_tier', 'pro');
       } else if (status === 'basic') {
           query = query.eq('subscription_tier', 'free');
       } else if (status === 'trial') {
           query = query.eq('subscription_status', 'trialing');
       } else if (status === 'active') {
           query = query.eq('subscription_status', 'active');
       } else if (status === 'gifted') {
           query = query.eq('subscription_status', 'gifted');
       } else if (status === 'suspended') {
           query = query.eq('subscription_status', 'suspended');
       }
    }

    const { data, error } = await query.limit(50);

    if (data) {
      // Fetch subscriptions to get period dates and gift info
      const userIds = data.map(u => u.id);
      const { data: subs } = await supabase
        .from('subscriptions')
        .select('user_id, current_period_start, current_period_end, gift_granted_at, gift_duration_days')
        .in('user_id', userIds);

      const subMap = new Map<string, { 
        start: string | null, 
        end: string | null,
        gift_granted_at: string | null,
        gift_duration_days: number | null
      }>(
          subs?.map(s => [s.user_id, { 
            start: s.current_period_start, 
            end: s.current_period_end,
            gift_granted_at: s.gift_granted_at,
            gift_duration_days: s.gift_duration_days
          }]) || []
      );

      const usersWithSub = data.map(u => {
          const sub = subMap.get(u.id);
          return {
              ...u,
              subscription_start_date: sub?.start,
              subscription_end_date: sub?.end,
              gift_granted_at: sub?.gift_granted_at,
              gift_duration_days: sub?.gift_duration_days
          };
      });

      setUsers(usersWithSub);
    }
    setLoading(false);
  }, [search, status]);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
        fetchUsers();
    }, 500);

    return () => clearTimeout(timer);
  }, [search, status, refreshKey, fetchUsers]);

  const handleRefresh = useCallback(() => {
    setRefreshKey(k => k + 1);
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-6 md:px-8 py-8">
      <div className="mb-8 space-y-1">
        <h2 className="text-4xl font-black tracking-tighter text-[#1A1A1A]">{t('admin.users.title')}</h2>
        <p className="text-lg font-medium text-gray-500">{t('admin.users.subtitle')}</p>
      </div>

      <div className="space-y-8">
        <UserFilters 
          search={search} 
          onSearchChange={setSearch}
          status={status}
          onStatusChange={setStatus}
        />

        <UserTable 
          users={users} 
          loading={loading} 
          onRefresh={handleRefresh}
        />
      </div>
    </div>
  );
}
